import { forwardRef } from 'react';

interface SectionProps {
  id: string;
  className?: string;
  children: React.ReactNode;
}

const Section = forwardRef<HTMLElement, SectionProps>(({ id, className = '', children }, ref) => (
  <section id={id} ref={ref} className={`py-14 md:py-20 px-4 md:px-6 ${className}`}>
    <div className="max-w-7xl mx-auto">{children}</div>
  </section>
));

Section.displayName = 'Section';

/* ───── #index: 主题索引 9项 ───── */
export function IndexSection() {
  const categories = [
    { name: '动作大片', icon: 'ri-sword-fill', count: '2,860部' },
    { name: '喜剧搞笑', icon: 'ri-emotion-happy-fill', count: '1,940部' },
    { name: '悬疑推理', icon: 'ri-search-eye-fill', count: '1,520部' },
    { name: '科幻冒险', icon: 'ri-rocket-fill', count: '1,380部' },
    { name: '爱情剧情', icon: 'ri-heart-fill', count: '1,760部' },
    { name: '恐怖惊悚', icon: 'ri-ghost-fill', count: '890部' },
    { name: '战争历史', icon: 'ri-flag-fill', count: '680部' },
    { name: '动画动漫', icon: 'ri-palette-fill', count: '1,120部' },
    { name: '纪录纪实', icon: 'ri-camera-fill', count: '540部' },
  ];

  return (
    <Section id="index" className="bg-background-100">
      <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-100 text-center mb-3">
        影片分类索引
      </h2>
      <p className="text-sm text-foreground-500 text-center max-w-lg mx-auto mb-10">
        全类型覆盖，总有一款适合您
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {categories.map((cat) => (
          <a
            key={cat.name}
            href="#index"
            className="group bg-background-50 rounded-xl border border-background-300/30 p-5 flex flex-col items-center text-center hover:border-primary-500/25 transition-colors cursor-pointer"
            onClick={(e) => e.preventDefault()}
          >
            <div className="w-12 h-12 rounded-xl bg-primary-500/8 flex items-center justify-center mb-3 group-hover:bg-primary-500/15 transition-colors">
              <i className={`${cat.icon} text-xl text-primary-400`}></i>
            </div>
            <span className="text-sm font-medium text-foreground-200 mb-1 whitespace-nowrap">{cat.name}</span>
            <span className="text-xs text-foreground-500">{cat.count}</span>
          </a>
        ))}
      </div>
    </Section>
  );
}

/* ───── #comments: 静态评论 5项 ───── */
export function CommentsSection() {
  const comments = [
    { name: '影迷小王', text: '用了91电影快两年了，片源更新是真的快，基本上院线下映一两周就有了，画质也很清晰，真心推荐给所有爱看电影的朋友。', rating: 5 },
    { name: '周末观影人', text: '之前找资源总要翻好几个网站，自从朋友推荐了91电影在线播放，一个站全搞定。秒播不卡顿这点太爽了，家里的百兆宽带跑满。', rating: 5 },
    { name: '剧荒终结者', text: '片库真的好全！很多冷门国产片都能找到，分类也很清晰。每天下班回家打开91电影在线观看，已经成了我的固定节目。', rating: 5 },
    { name: '电影发烧友阿杰', text: '作为一个看了上千部电影的人，91电影的片源质量让我惊喜。很多片子有多个清晰度可选，1080P体验完全不输付费平台。', rating: 4 },
    { name: '追剧小达人', text: '无广告这点真的绝了！之前用别的平台看个片子要等几十秒广告，91电影免费在线打开即播，体验太好了。已经安利给全公司同事。', rating: 5 },
  ];

  return (
    <Section id="comments" className="bg-background-50">
      <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-100 text-center mb-3">
        影迷心声
      </h2>
      <p className="text-sm text-foreground-500 text-center max-w-lg mx-auto mb-10">
        来自真实用户的使用评价
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {comments.map((c, i) => (
          <div
            key={i}
            className={`bg-background-100 rounded-2xl border border-background-300/30 p-6 ${i === 0 ? 'lg:col-span-2' : ''}`}
          >
            <div className="flex items-center gap-1 mb-3">
              {Array.from({ length: 5 }).map((_, j) => (
                <i
                  key={j}
                  className={`text-sm ${j < c.rating ? 'ri-star-fill text-accent-400' : 'ri-star-fill text-background-300'}`}
                ></i>
              ))}
            </div>
            <p className="text-sm text-foreground-400 leading-relaxed mb-4">{c.text}</p>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-primary-500/15 flex items-center justify-center">
                <span className="text-xs font-bold text-primary-400">{c.name[0]}</span>
              </div>
              <span className="text-sm font-medium text-foreground-300">{c.name}</span>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ───── #carousel: 焦点专题 6项 全部可见 ───── */
export function CarouselSection() {
  const features = [
    {
      title: '暑期档燃爆银幕',
      desc: '动作大片合集，热血沸腾的视觉盛宴，一口气刷到爽。包含《烈火追击》《孤胆英雄》等年度力作。',
      img: 'https://readdy.ai/api/search-image?query=Dark%20cinematic%20action%20movie%20atmosphere%20with%20explosive%20warm%20lighting%2C%20abstract%20film%20poster%20style%2C%20dramatic%20orange%20and%20crimson%20tones%2C%20dynamic%20energy%2C%20no%20recognizable%20faces%2C%20artistic%20cinematic%20composition%20with%20film%20texture&width=800&height=500&seq=carousel-01-movie&orientation=landscape',
    },
    {
      title: '悬疑烧脑专场',
      desc: '高智商犯罪与心理博弈，每一帧都让人屏住呼吸。精选《暗夜追踪》《深海迷踪》等口碑神作。',
      img: 'https://readdy.ai/api/search-image?query=Mysterious%20and%20suspenseful%20cinematic%20abstract%20scene%20with%20dark%20shadows%20and%20dramatic%20lighting%2C%20noir%20atmosphere%20with%20deep%20blacks%20and%20subtle%20warm%20highlights%2C%20film%20noir%20poster%20style%2C%20elegant%20minimal%20composition&width=800&height=500&seq=carousel-02-movie&orientation=landscape',
    },
    {
      title: '国产科幻崛起',
      desc: '国产科幻片近年佳作不断，特效与剧情双双在线。从太空史诗到近未来冒险，见证中国科幻力量。',
      img: 'https://readdy.ai/api/search-image?query=Futuristic%20sci-fi%20cinematic%20abstract%20art%20with%20cosmic%20and%20technological%20elements%2C%20dark%20space%20background%20with%20warm%20nebula%20accents%2C%20minimal%20science%20fiction%20poster%20style%2C%20elegant%20composition%20with%20soft%20glow&width=800&height=500&seq=carousel-03-movie&orientation=landscape',
    },
    {
      title: '喜剧欢乐时光',
      desc: '国产喜剧巅峰之作，笑点密集全程高能。无论独自放松还是朋友聚会，总有一部让你开怀大笑。',
      img: 'https://readdy.ai/api/search-image?query=Bright%20and%20cheerful%20cinematic%20abstract%20scene%20with%20warm%20golden%20lighting%2C%20playful%20and%20lively%20atmosphere%2C%20comedy%20film%20poster%20style%20with%20soft%20textures%2C%20dark%20background%20with%20warm%20accents%2C%20elegant%20composition&width=800&height=500&seq=carousel-04-movie&orientation=landscape',
    },
    {
      title: '爱情经典重温',
      desc: '华语爱情片经典与新作齐聚，从青涩初恋到深情守候，每一段故事都值得在深夜静静回味。',
      img: 'https://readdy.ai/api/search-image?query=Romantic%20and%20tender%20cinematic%20abstract%20art%20with%20soft%20warm%20lighting%2C%20dreamy%20atmosphere%20with%20rose%20and%20gold%20tones%2C%20romance%20film%20poster%20style%2C%20dark%20elegant%20background%2C%20minimal%20composition%20with%20gentle%20textures&width=800&height=500&seq=carousel-05-movie&orientation=landscape',
    },
    {
      title: '恐怖夜场特供',
      desc: '胆量测试专场，国产恐怖片从民俗传说中汲取灵感，氛围营造一流，深夜独享效果更佳。',
      img: 'https://readdy.ai/api/search-image?query=Eerie%20and%20atmospheric%20horror%20cinematic%20abstract%20scene%20with%20dark%20shadows%20and%20crimson%20undertones%2C%20haunting%20minimal%20composition%2C%20horror%20film%20poster%20style%2C%20deep%20blacks%20with%20subtle%20warm%20red%20accents%2C%20elegant%20and%20disturbing%20aesthetic&width=800&height=500&seq=carousel-06-movie&orientation=landscape',
    },
  ];

  return (
    <Section id="carousel" className="bg-background-100">
      <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-100 text-center mb-3">
        焦点专题
      </h2>
      <p className="text-sm text-foreground-500 text-center max-w-lg mx-auto mb-10">
        精选主题策划，发现更多好片
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {features.map((f, i) => (
          <div
            key={i}
            className="group bg-background-50 rounded-2xl border border-background-300/30 overflow-hidden hover:border-primary-500/20 transition-colors"
          >
            <div className="relative aspect-[16/10] overflow-hidden">
              <img
                src={f.img}
                alt={f.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent"></div>
            </div>
            <div className="p-5">
              <h3 className="text-base font-heading font-bold text-foreground-100 mb-2">{f.title}</h3>
              <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">{f.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}